import React, { useEffect, useState } from "react";
import { withModel } from "@lubbezposrednio-frontend/core";
import { ProfileViewModel } from "./ProfileViewModel";
import { SecurityCard } from "./components/SecurityCard";
import { observer } from "mobx-react-lite";
import { UserProfile } from "./components/UserProfile/UserProfile";
import { TotpCard } from "./components/TotpCard";
import { ActiveSessionsCard } from "./components/ActiveSessionsCard";
import { RecoveryCodesCard } from "./components/RecoveryCodesCard";
import { PasskeyCard } from "./components/PasskeyCard";
import { IdentityKingCard } from "./components/IdentityKingCard";
import { OrganizationProfile } from "./components/OrganizationProfile/OrganizationProfile";
import { Avatar, Box, Button, Flex, Heading, HStack, Icon, SimpleGrid, Spinner, Switch, Text, Textarea, useToast, VStack } from "@chakra-ui/react";
import {
  PiLockDuotone as FiLock,
  PiTrashDuotone as FiTrash2,
  PiCopyDuotone as FiCopy,
  PiArrowSquareOutDuotone as FiExternalLink,
  PiUserDuotone as FiUser,
  PiFileTextDuotone as FiFileText,
  PiShieldCheckDuotone as FiShield,
  PiGlobeDuotone as FiGlobe,
  PiEyeDuotone as FiEye,
  PiUserPlusDuotone as FiUserPlus,
  PiGearDuotone,
  PiSealCheckDuotone,
  PiEnvelopeSimpleDuotone,
  PiUsersThreeDuotone,
  PiIdentificationCardDuotone,
  PiCaretRightBold
} from "react-icons/pi";
import type { IconType } from "react-icons";
import { GLASS, SCROLL_FOG, drgba } from "../../components/glassUi";
import { Link, useNavigate } from "react-router-dom";
import { ConsentsViewModel } from "../ConsentsView/ConsentsViewModel";
import { Consent } from "../ConsentsView/components/Consent";

const ConsentsSection: React.FC = observer(() => {
  const [model] = useState(() => new ConsentsViewModel());

  useEffect(() => {
    model.getConsents();
  }, []);

  return (
    <VStack align="stretch" spacing="14px" w="100%">
      {model.consents.map(consent => (
        <Consent key={consent.id} {...consent} model={model} />
      ))}
    </VStack>
  );
});

type SectionKey = "profil" | "zgody" | "bezpieczenstwo" | "publiczny";

const ACCOUNT_NAV: { key: SectionKey; label: string; icon: IconType }[] = [
  { key: "profil", label: "Profil", icon: FiUser },
  { key: "zgody", label: "Zgody", icon: FiFileText },
  { key: "bezpieczenstwo", label: "Bezpieczeństwo", icon: FiShield },
  { key: "publiczny", label: "Profil publiczny", icon: FiGlobe }
];

const SecuritySection: React.FC<{ model: ProfileViewModel }> = observer(({ model }) => {
  const toast = useToast();
  const navigate = useNavigate();

  const twoFactorLocked = model.apiAccess; // api_access ⇒ wymuszone, nie da się wyłączyć
  const twoFactorOn = twoFactorLocked || model.twoFactorEnabled;
  // Metoda „niedrenująca" (passkey/authenticator/portfel) — przy niej „zawsze pytaj" jest sensowne (nie e-mail).
  const hasFrictionless2FA = model.totpEnabled || model.identityKey || model.passkeysCount > 0;

  return (
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing="16px" w="100%" alignItems="start">
      <IdentityKingCard model={model} />

      <PasskeyCard />

      <SecurityCard
        icon={FiShield}
        full
        title="Logowanie dwuetapowe (2FA)"
        subtitle={
          twoFactorLocked
            ? "Wymagane dla kont z dostępem do API — nie można wyłączyć."
            : "Przy logowaniu z nowego urządzenia poprosimy o drugi składnik. Dostępne metody: zatwierdzenie w aplikacji, kod z aplikacji authenticator albo kod z e-maila."
        }
        action={
          model.twoFactorSaving ? (
            <Spinner size="sm" color="primary" />
          ) : (
            <Switch colorScheme="purple" isChecked={twoFactorOn} isDisabled={twoFactorLocked} onChange={e => model.toggleTwoFactor(e.target.checked, toast)} />
          )
        }
      >
        {twoFactorOn && (
          <Box pt="14px" mt="2px" borderTop="1px solid rgba(67,0,152,0.07)">
            <Flex justify="space-between" align="center" gap="12px">
              <Box>
                <Text fontSize="14px" fontWeight="600" color="gray.700">Pytaj przy każdym logowaniu</Text>
                <Text fontSize="12px" color="gray.400">Domyślnie pamiętamy zaufane urządzenie i nie pytamy ponownie. Włącz, by wymagać 2FA zawsze.</Text>
              </Box>
              <Switch colorScheme="purple" isChecked={model.twoFactorAlways} isDisabled={model.twoFactorSaving} onChange={e => model.toggleTwoFactorAlways(e.target.checked, toast)} />
            </Flex>
            <Box mt="10px" p="9px 12px" borderRadius="10px" bg={hasFrictionless2FA ? "rgba(47,206,37,0.08)" : "rgba(229,31,68,0.06)"}>
              <Text fontSize="12px" color={hasFrictionless2FA ? "#1f9e18" : "#9a1b30"} lineHeight="1.5">
                {hasFrictionless2FA
                  ? "Masz passkey lub authenticator — to wygodne i nie obciąża (w przeciwieństwie do kodów e-mail), więc tryb „zawsze” jest tu sensowny."
                  : "Bez passkey/authenticatora każde logowanie wyśle kod e-mailem — to może męczyć. Najpierw skonfiguruj passkey lub authenticator (poniżej)."}
              </Text>
            </Box>
          </Box>
        )}
      </SecurityCard>

      <TotpCard model={model} />

      <ActiveSessionsCard />

      <RecoveryCodesCard />

      <SecurityCard
        icon={FiLock}
        accent="#475569"
        title="Hasło"
        subtitle="Zmienisz je bezpiecznie przez link wysłany na Twój adres e-mail."
      >
        <Button as={Link} to="/zapomnialem-hasla" variant="outline" borderColor="#475569" color="#475569" borderRadius="12px" fontWeight="700" leftIcon={<FiLock />} _hover={{ bg: "rgba(71,85,105,0.06)" }}>
          Zmień hasło
        </Button>
      </SecurityCard>

      <SecurityCard
        icon={FiTrash2}
        danger
        title="Usuń konto"
        subtitle="Po usunięciu konto zostanie zablokowane — nie zalogujesz się, nie wrócisz do swoich głosowań ani nie założysz nowego konta na ten sam adres e-mail. Tej operacji nie da się cofnąć."
      >
        <Button onClick={() => model.deactivateAccount(toast, navigate)} bg="#e51f44" color="white" borderRadius="12px" fontWeight="700" leftIcon={<FiTrash2 />} _hover={{ bg: "#c41a3a" }}>
          Usuń konto
        </Button>
      </SecurityCard>
    </SimpleGrid>
  );
});

const PublicProfileSection: React.FC<{ model: ProfileViewModel }> = observer(({ model }) => {
  const toast = useToast();
  // @ jest bezpieczny w ścieżce URL — zostawiamy dosłowny, bez %40 (encodeURIComponent kodowałby @).
  const profileUrl = model.profileRef ? `${window.location.origin}/profil/${model.profileRef}` : "";
  const toggle = (label: string, desc: string, value: boolean, key: "profile_show_votings" | "profile_show_groups" | "profile_show_email" | "profile_show_org_details" | "profile_show_address" | "profile_show_posts") => (
    <Flex justify="space-between" align="center" gap="12px">
      <Box>
        <Text fontSize="14px" fontWeight="600" color="gray.700">
          {label}
        </Text>
        <Text fontSize="12px" color="gray.400">
          {desc}
        </Text>
      </Box>
      <Switch colorScheme="purple" isChecked={value} onChange={e => model.saveProfileSettings({ [key]: e.target.checked }, toast)} />
    </Flex>
  );

  return (
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing="16px" w="100%" alignItems="start">
      <SecurityCard
        icon={FiGlobe}
        full
        title="Profil publiczny"
        subtitle="Udostępnij swój profil — z publicznymi głosowaniami i grupami. Inni mogą Cię obserwować."
        action={
          model.profileSaving ? (
            <Spinner size="sm" color="primary" />
          ) : (
            <Switch colorScheme="purple" isChecked={model.profilePublic} onChange={e => model.saveProfileSettings({ profile_public: e.target.checked }, toast)} />
          )
        }
      >
        {model.profilePublic && model.profileRef && (
          <HStack bg="rgba(67,0,152,0.04)" borderRadius="12px" p="10px 14px">
            <Text fontSize="13px" fontWeight="700" color="primary" noOfLines={1} flex="1">
              {model.profileRef}
            </Text>
            <Button
              size="xs"
              leftIcon={<FiCopy />}
              variant="outline"
              borderColor="primary"
              color="primary"
              onClick={() => {
                navigator.clipboard?.writeText(profileUrl);
                toast({ title: "Skopiowano link profilu", status: "success", duration: 1500, isClosable: true });
              }}
            >
              Kopiuj link
            </Button>
            <Button as={Link} to={`/profil/${model.profileRef}`} size="xs" leftIcon={<FiExternalLink />} bg="primary" color="white" _hover={{ bg: "#360078" }}>
              Zobacz
            </Button>
          </HStack>
        )}
      </SecurityCard>

      <SecurityCard
        icon={FiUserPlus}
        full
        accent="#2563EB"
        title="Dodawanie do grup"
        subtitle="Pozwól organizatorom dodawać Cię do grup po Twoim adresie e-mail. Po wyłączeniu Twój adres jest pomijany przy dodawaniu."
        action={<Switch colorScheme="purple" isChecked={model.allowGroupAdd} onChange={e => model.saveProfileSettings({ allow_group_add: e.target.checked }, toast)} />}
      />

      {model.profilePublic && (
        <SecurityCard icon={FiEye} full accent="#0E7A9E" title="Co pokazywać">
          <Box mb="18px">
            <Text fontSize="13px" fontWeight="700" color="primary" mb="6px">
              Krótki opis (bio)
            </Text>
            <Textarea
              value={model.profileBio}
              onChange={e => model.setProfileBio(e.target.value)}
              onBlur={() => model.saveProfileSettings({ profile_bio: model.profileBio || null }, toast)}
              placeholder="Kilka słów o sobie lub organizacji"
              borderRadius="12px"
              focusBorderColor="primary"
              rows={3}
            />
          </Box>
          <VStack align="stretch" spacing="14px" divider={<Box h="1px" bg="rgba(67,0,152,0.06)" />}>
            {toggle("Posty", "Pokaż Twoje publiczne posty (Społeczność) na profilu.", model.profileShowPosts, "profile_show_posts")}
            {toggle("Publiczne głosowania", "Pokaż listę Twoich publicznych głosowań.", model.profileShowVotings, "profile_show_votings")}
            {toggle("Publiczne grupy", "Pokaż grupy oznaczone jako widoczne na profilu.", model.profileShowGroups, "profile_show_groups")}
            {toggle("Adres e-mail", "Pokaż e-mail kontaktowy na profilu.", model.profileShowEmail, "profile_show_email")}
            {model.isOrganization && toggle("Dane rejestrowe", "Pokaż NIP, REGON i KRS na profilu.", model.profileShowOrgDetails, "profile_show_org_details")}
            {model.isOrganization && toggle("Adres siedziby", "Pokaż adres siedziby organizacji na profilu.", model.profileShowAddress, "profile_show_address")}
          </VStack>
        </SecurityCard>
      )}
    </SimpleGrid>
  );
});

const SECTION_SUB: Record<SectionKey, string> = {
  profil: "Twoje dane, zdjęcie i informacje kontaktowe.",
  zgody: "Regulamin, polityka prywatności i pozostałe zgody.",
  bezpieczenstwo: "2FA, passkey, sesje, hasło i usuwanie konta.",
  publiczny: "Widoczność profilu, obserwowanie i co pokazywać."
};

// ── Rail (styl pulpitu) ──
const RailHeading: React.FC<{ icon: React.ElementType; title: string; color?: string }> = ({ icon, title, color = "#430098" }) => (
  <HStack spacing="8px" mb="11px" minW={0}>
    <Icon as={icon} boxSize="18px" color={color} flexShrink={0} />
    <Text fontSize="12px" fontWeight="800" color="#5a5468" textTransform="uppercase" letterSpacing="0.08em" noOfLines={1}>{title}</Text>
  </HStack>
);
const RailDivider = () => <Box borderTop="1px solid rgba(67,0,152,0.08)" my="20px" />;
const MetaRow: React.FC<{ icon: React.ElementType; color: string; label: string; value: React.ReactNode }> = ({ icon, color, label, value }) => (
  <HStack justify="space-between" spacing="10px">
    <HStack spacing="8px" color="#6b6480" minW={0}><Icon as={icon} boxSize="14px" color={color} flexShrink={0} /><Text fontSize="13px" noOfLines={1}>{label}</Text></HStack>
    <Text fontSize="13px" fontWeight="800" color="#241536" textAlign="right" noOfLines={1} flexShrink={0}>{value}</Text>
  </HStack>
);

// Rail konta: tożsamość + fakty + szybkie akcje.
const AccountRail: React.FC<{ model: ProfileViewModel; onSection: (s: SectionKey) => void }> = observer(({ model, onSection }) => {
  const name = (model.isOrganization ? model.organizationName : `${model.firstName} ${model.lastName}`.trim()) || "Twoje konto";
  return (
    <>
      {/* Tożsamość */}
      <Flex direction="column" align="center" textAlign="center" gap="10px">
        <Avatar boxSize="72px" name={name} src={model.userPhotoPreview ?? undefined} bg="primary" color="white" fontWeight="800" />
        <Box minW={0} w="100%">
          <HStack spacing="7px" justify="center" wrap="wrap">
            <Text fontSize="16px" fontWeight="900" color="#1b1030" noOfLines={2} lineHeight="1.15" letterSpacing="-0.01em">{name}</Text>
          </HStack>
          {model.isUserVerified && (
            <HStack spacing="4px" justify="center" mt="6px">
              <HStack spacing="4px" bg={drgba("#1d9bf0", 0.12)} color="#1478bd" px="9px" py="3px" borderRadius="full"><Icon as={PiSealCheckDuotone} boxSize="13px" /><Text fontSize="12px" fontWeight="800">Zweryfikowany</Text></HStack>
            </HStack>
          )}
          {model.profileRef && <Text fontSize="12.5px" color="#9a94ab" fontWeight="700" fontFamily="mono" mt="6px" noOfLines={1}>{model.profileRef}</Text>}
        </Box>
        {model.profileRef && (
          <Button as={Link} to={`/profil/${model.profileRef}`} size="sm" w="100%" leftIcon={<Icon as={FiExternalLink} />} variant="outline" borderColor="rgba(67,0,152,0.25)" color="primary" borderRadius="12px" fontWeight="800" _hover={{ bg: drgba("#430098", 0.05), textDecoration: "none" }}>Zobacz profil publiczny</Button>
        )}
      </Flex>

      <RailDivider />

      {/* Fakty */}
      <RailHeading icon={PiIdentificationCardDuotone} title="Konto" color="#2563eb" />
      <VStack align="stretch" spacing="11px">
        <MetaRow icon={PiIdentificationCardDuotone} color="#2563eb" label="Typ" value={model.isOrganization ? "Organizacja" : "Osoba"} />
        <MetaRow icon={PiEnvelopeSimpleDuotone} color="#7b2ff7" label="E-mail" value={model.email} />
        <MetaRow icon={PiUsersThreeDuotone} color="#10b981" label="Obserwujący" value={String(model.followersCount)} />
      </VStack>

      <RailDivider />

      {/* Szybkie akcje */}
      <RailHeading icon={PiGearDuotone} title="Szybkie akcje" color="#e51f44" />
      <VStack align="stretch" spacing="8px">
        <Button onClick={() => onSection("bezpieczenstwo")} justifyContent="flex-start" leftIcon={<Icon as={FiShield} boxSize="17px" color="#7b2ff7" />} rightIcon={<Icon as={PiCaretRightBold} boxSize="12px" color="#c0b9d0" />} variant="ghost" color="#3f3a4d" fontWeight="800" size="sm" borderRadius="11px" _hover={{ bg: drgba("#430098", 0.05) }}><Box flex="1" textAlign="left">Bezpieczeństwo</Box></Button>
        <Button onClick={() => onSection("publiczny")} justifyContent="flex-start" leftIcon={<Icon as={FiGlobe} boxSize="17px" color="#2563eb" />} rightIcon={<Icon as={PiCaretRightBold} boxSize="12px" color="#c0b9d0" />} variant="ghost" color="#3f3a4d" fontWeight="800" size="sm" borderRadius="11px" _hover={{ bg: drgba("#2563eb", 0.06) }}><Box flex="1" textAlign="left">Profil publiczny</Box></Button>
        <Button as={Link} to="/zapomnialem-hasla" justifyContent="flex-start" leftIcon={<Icon as={FiLock} boxSize="17px" color="#475569" />} variant="ghost" color="#3f3a4d" fontWeight="800" size="sm" borderRadius="11px" _hover={{ bg: "rgba(71,85,105,0.06)", textDecoration: "none" }}><Box flex="1" textAlign="left">Zmień hasło</Box></Button>
      </VStack>
    </>
  );
});

interface ProfileViewProps {
  model: ProfileViewModel;
}

export const ProfileView: React.FC<ProfileViewProps> = observer(({ model }) => {
  const toast = useToast();
  const [section, setSection] = useState<SectionKey>("profil");

  useEffect(() => {
    document.title = "Konto";
    model.getProfileData(toast);
  }, []);

  const content = (
    <Box borderRadius="24px" p={{ base: "14px", md: "18px" }} sx={GLASS}>
      {section === "profil" &&
        (model.isOrganization ? (
          <>
            <OrganizationProfile model={model} />
            <Heading fontSize="18px" fontWeight="800" color="primary" mb="18px" mt="32px">
              Profil i dane reprezentanta
            </Heading>
            <UserProfile model={model} isOrganization />
          </>
        ) : (
          <UserProfile model={model} />
        ))}
      {section === "zgody" && <ConsentsSection />}
      {section === "bezpieczenstwo" && <SecuritySection model={model} />}
      {section === "publiczny" && <PublicProfileSection model={model} />}
    </Box>
  );

  return (
    <Flex align="flex-start">
      <Box flex="1" minW={0}>
        <Box w="100%" maxW="1000px" mx="auto" px={{ base: "20px", md: "44px" }} py={{ base: "24px", md: "36px" }}>
          {/* Header glass + pigułki-taby */}
          <Box borderRadius="24px" mb="16px" sx={GLASS}>
            <Box px={{ base: "18px", md: "26px" }} pt={{ base: "18px", md: "22px" }} pb="14px">
              <HStack spacing="14px" minW={0}>
                <Flex w={{ base: "50px", md: "56px" }} h={{ base: "50px", md: "56px" }} borderRadius="16px" bg={drgba("#7b2ff7", 0.12)} align="center" justify="center" flexShrink={0}>
                  <Icon as={PiGearDuotone} boxSize="28px" color="#7b2ff7" />
                </Flex>
                <Box minW={0}>
                  <Heading fontSize={{ base: "24px", md: "28px" }} fontWeight="900" color="#1b1030" lineHeight="1.05" letterSpacing="-0.02em">Ustawienia konta</Heading>
                  <Text fontSize="13.5px" color="#6b6480" mt="3px" noOfLines={1}>{SECTION_SUB[section]}</Text>
                </Box>
              </HStack>

              <HStack spacing="3px" mt="16px" pt="12px" borderTop="1px solid rgba(24,12,48,0.07)" overflowX="auto" sx={{ scrollbarWidth: "none", "::-webkit-scrollbar": { display: "none" } }}>
                {ACCOUNT_NAV.map(item => {
                  const active = section === item.key;
                  return (
                    <Button key={item.key} onClick={() => setSection(item.key)} leftIcon={<Icon as={item.icon} boxSize="15px" />} size="sm" borderRadius="full" fontWeight="800" px="15px"
                      color={active ? "#7b2ff7" : "#6b6480"} bg={active ? "rgba(123,47,247,0.1)" : "transparent"} boxShadow="none"
                      _hover={{ bg: active ? "rgba(123,47,247,0.16)" : "rgba(24,12,48,0.06)" }} flexShrink={0}>
                      {item.label}
                    </Button>
                  );
                })}
              </HStack>
            </Box>
          </Box>

          {content}

          {/* Rail zwinięty <xl */}
          <Box display={{ base: "block", xl: "none" }} mt="18px" borderRadius="22px" px="18px" py="20px" sx={GLASS}>
            <AccountRail model={model} onSection={setSection} />
          </Box>
        </Box>
      </Box>

      {/* ── GŁÓWNY PRAWY RAIL — pływający, pełnowysokościowy ── */}
      <Box as="aside" display={{ base: "none", xl: "block" }} w="384px" flexShrink={0} alignSelf="stretch">
        <Box position="sticky" top="0" h="100vh" p="12px">
          <Box position="relative" h="100%" borderRadius="26px" overflow="hidden" sx={GLASS}>
            <Box position="absolute" inset={0} pointerEvents="none" sx={{ background: "radial-gradient(58% 40% at 50% 20%, rgba(124,58,237,0.14), transparent 72%)" }} />
            <VStack position="relative" align="stretch" spacing="0" h="100%" px="16px" py="20px" sx={{ ...SCROLL_FOG, maskImage: "linear-gradient(to bottom, #000 0, #000 calc(100% - 24px), transparent 100%)", WebkitMaskImage: "linear-gradient(to bottom, #000 0, #000 calc(100% - 24px), transparent 100%)" }}>
              <AccountRail model={model} onSection={setSection} />
            </VStack>
          </Box>
        </Box>
      </Box>
    </Flex>
  );
});

const ProfileViewWithModel = withModel<ProfileViewModel>(ProfileView, ProfileViewModel);

export default ProfileViewWithModel;
